/**
 * Quick View Modal — fetches the product `quick-view` template via Liquid,
 * injects the section content into a native <dialog>, lets the customer
 * pick a variant and add to cart without leaving the listing.
 *
 * Open flow:
 *   - Any element on the page can dispatch `quick-view:open` on `document`
 *     with `detail: { handle: 'product-handle' }`.
 *   - The single <quick-view-modal> instance in the layout opens its dialog,
 *     fetches /products/{handle}?view=quick-view (which renders the minimal
 *     quick-view layout), extracts the first <quick-view-form>, and injects it.
 *
 * <quick-view-form> markup contract (rendered by `sections/main-quick-view.liquid`):
 *   data-option-count       — number of product options (1–3)
 *   data-variants           — JSON array: [{ id, available, options: [...], price, price_html, sku }, ...]
 *   [data-variant-pill][data-option-index][data-value]  — variant choice buttons
 *   [data-add-to-cart][data-variant-id]                 — submit button
 *   [data-quick-view-price]                             — money element (optional)
 */
import { addToCart } from "@theme/cart-add";

class QuickViewModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("[data-modal]");
    this.contentEl = this.querySelector("[data-content]");
    this.closeBtn = this.querySelector("[data-close]");
    if (!this.dialog || !this.contentEl) return;

    this.closeBtn?.addEventListener("click", () => this.close());
    this.dialog.addEventListener("click", (e) => {
      const card = this.dialog.querySelector("[data-modal-card]");
      if (card && !card.contains(e.target)) this.close();
    });
    this.dialog.addEventListener("close", () => {
      document.body.classList.remove("hhs-modal-open");
      this.contentEl.innerHTML = this.#loadingMarkup();
    });

    document.addEventListener("quick-view:open", (e) => {
      const handle = e.detail?.handle;
      if (handle) this.open(handle);
    });
  }

  async open(handle) {
    if (this._inflight === handle) return;
    this._inflight = handle;

    this.contentEl.innerHTML = this.#loadingMarkup();
    if (!this.dialog.open && typeof this.dialog.showModal === "function") {
      this.dialog.showModal();
    }
    document.body.classList.add("hhs-modal-open");

    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}?view=quick-view`, {
        headers: { Accept: "text/html" },
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const form = doc.querySelector("quick-view-form");
      if (!form) throw new Error("quick-view-form not found in response");
      this.contentEl.innerHTML = form.outerHTML;
      this.#initGallery();
    } catch (err) {
      console.error("Quick view fetch failed:", err);
      this.contentEl.innerHTML = `
        <div class="p-10 text-center">
          <p class="font-body text-ink mb-4">Greška pri učitavanju proizvoda.</p>
          <a href="/products/${encodeURIComponent(handle)}" class="font-display uppercase text-button tracking-button text-pink-deep underline">
            Vidi sve detalje →
          </a>
        </div>`;
    } finally {
      this._inflight = null;
    }
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  #initGallery() {
    const track = this.contentEl.querySelector("[data-qv-track]");
    if (!track) return;

    // The modal's markup arrives through innerHTML, where the autoplay
    // attribute alone doesn't reliably start a video. Asking once costs
    // nothing and the promise rejects harmlessly if the browser says no.
    track.querySelectorAll("video[autoplay]").forEach((video) => {
      video.play().catch(() => {});
    });

    const slides = track.querySelectorAll(".hhs-qv-gallery__slide");
    if (slides.length <= 1) return;

    let current = 0;
    const total = slides.length;
    const dots = this.contentEl.querySelectorAll("[data-qv-dot]");
    const prevBtn = this.contentEl.querySelector("[data-qv-prev]");
    const nextBtn = this.contentEl.querySelector("[data-qv-next]");

    const goTo = (index) => {
      current = ((index % total) + total) % total;
      track.style.transform = `translateX(-${current * 100}%)`;
      dots.forEach((d, i) => d.classList.toggle("is-active", i === current));
    };

    prevBtn?.addEventListener("click", (e) => { e.preventDefault(); goTo(current - 1); });
    nextBtn?.addEventListener("click", (e) => { e.preventDefault(); goTo(current + 1); });
    dots.forEach((d, i) => d.addEventListener("click", () => goTo(i)));

    let touchStartX = 0;
    track.addEventListener("touchstart", (e) => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    track.addEventListener("touchend", (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) goTo(diff > 0 ? current + 1 : current - 1);
    }, { passive: true });

    let mouseStartX = 0;
    let dragActive = false;
    let dragMoved = false;

    track.addEventListener("mousedown", (e) => {
      mouseStartX = e.clientX;
      dragActive = true;
      dragMoved = false;
      track.style.cursor = "grabbing";
      e.preventDefault();
    });

    document.addEventListener("mousemove", (e) => {
      if (!dragActive) return;
      if (Math.abs(e.clientX - mouseStartX) > 5) dragMoved = true;
    });

    document.addEventListener("mouseup", (e) => {
      if (!dragActive) return;
      dragActive = false;
      track.style.cursor = "grab";
      const diff = mouseStartX - e.clientX;
      if (Math.abs(diff) > 40) goTo(diff > 0 ? current + 1 : current - 1);
    });

    track.addEventListener("click", (e) => {
      if (dragMoved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    track.style.cursor = "grab";
  }

  #loadingMarkup() {
    return `<div class="p-12 text-center font-major-mono text-xs text-noir-violet">// učitavanje...</div>`;
  }
}

class QuickViewForm extends HTMLElement {
  connectedCallback() {
    this.atcButton = this.querySelector("[data-add-to-cart]");
    this.priceEl = this.querySelector("[data-quick-view-price-amount]");
    this.optionCount = parseInt(this.dataset.optionCount || "1", 10);
    try {
      this.variants = JSON.parse(this.dataset.variants || "[]");
    } catch {
      this.variants = [];
    }

    this.querySelectorAll("[data-variant-pill]").forEach((pill) => {
      pill.addEventListener("click", (e) => {
        e.preventDefault();
        if (pill.classList.contains("is-unavailable")) return;
        this.#selectPill(pill);
      });
    });

    this.atcButton?.addEventListener("click", (e) => {
      e.preventDefault();
      this.#addToCart();
    });
  }

  #selectPill(pill) {
    const idx = parseInt(pill.dataset.optionIndex || "0", 10);
    this.querySelectorAll(`[data-variant-pill][data-option-index="${idx}"]`).forEach((p) =>
      p.classList.remove("is-active"),
    );
    pill.classList.add("is-active");

    const selected = [];
    for (let i = 0; i < this.optionCount; i++) {
      const active = this.querySelector(`[data-variant-pill][data-option-index="${i}"].is-active`);
      selected.push(active ? active.dataset.value : null);
    }

    const variant = this.variants.find((v) => {
      for (let i = 0; i < this.optionCount; i++) {
        if (selected[i] != null && v.options[i] !== selected[i]) return false;
      }
      return true;
    });

    if (variant && this.atcButton) {
      this.atcButton.dataset.variantId = variant.id;
      this.atcButton.disabled = !variant.available;
      this.atcButton.classList.toggle("is-sold-out", !variant.available);
      if (this.priceEl && variant.price_html) {
        this.priceEl.innerHTML = variant.price_html;
      }
      // Each size is its own article code, as on the product page.
      const skuEl = this.querySelector("[data-qv-sku]");
      if (skuEl && variant.sku) skuEl.textContent = variant.sku;
    }
  }

  async #addToCart() {
    if (!this.atcButton || this.atcButton.disabled) return;
    const id = this.atcButton.dataset.variantId;
    if (!id) return;

    const originalHTML = this.atcButton.innerHTML;
    this.atcButton.disabled = true;
    this.atcButton.innerHTML = "DODAJEM…";

    try {
      await addToCart({ id });
      this.atcButton.innerHTML = "DODATO ✓";
      const modal = document.querySelector("quick-view-modal");
      setTimeout(() => modal?.close(), 700);
    } catch (err) {
      console.error("Quick view ATC failed:", err);
      this.atcButton.innerHTML = "GREŠKA — POKUŠAJ PONOVO";
      setTimeout(() => {
        this.atcButton.innerHTML = originalHTML;
        this.atcButton.disabled = false;
      }, 2000);
    }
  }
}

customElements.define("quick-view-modal", QuickViewModal);
customElements.define("quick-view-form", QuickViewForm);

export { QuickViewModal, QuickViewForm };
